import type { FC } from "react";
import { useMemo } from "react";

import _ from "lodash";
import styled from "styled-components";

import { NOT_AVAILABLE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { ETypes } from "app/pages/industry-analysis/players/table/bubble-chart/types";
import { EScrollTo } from "app/pages/industry-analysis/players/types";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import Header from "../../key-indicators/single/common/Header";
import type { IDifficulty } from "../common/types";
import { EIndustryAssess, ETotalScore, EcubeCategory } from "../common/types";
import { getAssessColor, getAssessName } from "../utils/utils";

import Cube from "./components/Cube";
import TotalVisits from "./components/DifficultyStatus";
import Divider from "./components/Divider";

const MarketDifficultyWrapper = styled.div`
    width: auto;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const TotalVisitsContainer = styled.div`
    width: 50%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px;
`;

const CubeContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 50%;
    padding: 8px;
`;

const ContentContainer = styled.div`
    display: flex;
    justify-content: space-between;
    flex-grow: 1;

    @media (max-width: 1100px) {
        ${TotalVisitsContainer} {
            width: 44%;
        }
        ${CubeContainer} {
            width: 56%;
        }
    }
`;

const CubeRow = styled.div`
    display: flex;
    height: 100%;
    gap: 8px;
`;

const NoDataContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    align-content: center;
    height: 300px;
`;

interface IMarketDifficulty {
    data: IDifficulty;
    isLoading: boolean;
}

const USD_CURRENCY = "$";

const MarketDifficulty: FC<IMarketDifficulty> = ({ data, isLoading }) => {
    const getFilteredValue = (num: string | number, isPercentage = false) => {
        // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
        return isPercentage ? percentageSignFilter()(num, 2) : abbrNumberFilter()(num, 0);
    };

    const onCubeClick = (cubeCategory: EcubeCategory, scrollTo?: EScrollTo, bubbleChartDisplayType?: string) => {
        allTrackers.dispatchTrackEvent("Internal link", "click", "Market Difficulty", {
            DifficultyCube: cubeCategory,
        });

        // Get current route params while preserving existing parameters
        const currentParams = proNavigator.getParams();
        void proNavigator.go("market_research_players", {
            ...currentParams,
            scrollTo,
            bubbleChartDisplayType: bubbleChartDisplayType,
        });
    };

    const initData = (): IDifficulty => {
        const emptyObj = {
            Value: NOT_AVAILABLE,
            IndustryAssess: EIndustryAssess.NO_DATA,
        };

        if (!data) {
            return {
                Loyalty: emptyObj,
                TotalScore: ETotalScore.NO_DATA,
                Consolidation: emptyObj,
                Cost: emptyObj,
                Branding: emptyObj,
            };
        }
        return data;
    };

    const { Loyalty, TotalScore, Consolidation, Cost, Branding } = initData();

    const usdCurrency = useMemo(() => {
        if (Cost.Value !== NOT_AVAILABLE) {
            return USD_CURRENCY;
        }
        return "";
    }, [Cost.Value]);

    return (
        <MarketDifficultyWrapper data-automation="MarketDifficulty">
            <Header
                title={translate("industryoverview.market.difficulty.header.title")}
                tooltip={translate("industryoverview.market.difficulty.title.tooltip")}
                isLoading={isLoading}
                showFrom={false}
                showLatest={true}
                displayBetaLabel={true}
            />
            <ContentContainer>
                <TotalVisitsContainer>
                    <TotalVisits
                        status={TotalScore}
                        isLoading={isLoading}
                    />
                </TotalVisitsContainer>
                <Divider />
                <CubeContainer>
                    <CubeRow>
                        <Cube
                            title={translate("industryoverview.market.difficulty.cube.loyalty")}
                            data={getFilteredValue(Loyalty.Value, true)}
                            tooltip={translate("industryoverview.market.difficulty.cube.loyalty.tooltip")}
                            isLoading={isLoading}
                            subtitle={translate("industryoverview.market.difficulty.cube.loyalty.subtitle")}
                            averageStatus={getAssessName(Loyalty.IndustryAssess)}
                            averageStatusColor={getAssessColor(Loyalty.IndustryAssess)}
                            onClick={() =>
                                onCubeClick(EcubeCategory.LOYALTY, EScrollTo.QUADRANT, ETypes[ETypes.Loyalty])
                            }
                        />
                        {!_.isEmpty(Consolidation) ? (
                            <Cube
                                title={translate("industryoverview.market.difficulty.cube.consolidation")}
                                data={getFilteredValue(Consolidation.Value, true)}
                                tooltip={translate("industryoverview.market.difficulty.cube.consolidation.tooltip")}
                                isLoading={isLoading}
                                subtitle={translate("industryoverview.market.difficulty.cube.consolidation.subtitle")}
                                averageStatus={getAssessName(Consolidation.IndustryAssess)}
                                averageStatusColor={getAssessColor(Consolidation.IndustryAssess)}
                                onClick={() => onCubeClick(EcubeCategory.CONSOLIDATION, EScrollTo.MARKET_SHARES)}
                            />
                        ) : null}
                    </CubeRow>
                    <CubeRow>
                        <Cube
                            title={translate("industryoverview.market.difficulty.cube.cost")}
                            data={`${usdCurrency}${getFilteredValue(Cost.Value)}`}
                            tooltip={translate("industryoverview.market.difficulty.cube.cost.tooltip")}
                            isLoading={isLoading}
                            subtitle={translate("industryoverview.market.difficulty.cube.cost.subtitle")}
                            averageStatus={getAssessName(Cost.IndustryAssess)}
                            averageStatusColor={getAssessColor(Cost.IndustryAssess)}
                            onClick={() =>
                                onCubeClick(EcubeCategory.COST, EScrollTo.QUADRANT, ETypes[ETypes.CostEfficiency])
                            }
                        />
                        <Cube
                            title={translate("industryoverview.market.difficulty.cube.branding")}
                            data={getFilteredValue(Branding.Value, true)}
                            tooltip={translate("industryoverview.market.difficulty.cube.branding.tooltip")}
                            isLoading={isLoading}
                            subtitle={translate("industryoverview.market.difficulty.cube.branding.subtitle")}
                            averageStatus={getAssessName(Branding.IndustryAssess)}
                            averageStatusColor={getAssessColor(Branding.IndustryAssess)}
                            onClick={() =>
                                onCubeClick(EcubeCategory.BRANDING, EScrollTo.QUADRANT, ETypes[ETypes.BrandStrength])
                            }
                        />
                    </CubeRow>
                </CubeContainer>
            </ContentContainer>
        </MarketDifficultyWrapper>
    );
};

export default MarketDifficulty;
